import { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ShoppingCart, Plus, Minus, X, Tag, Copy, Check, CreditCard, Loader2, ArrowRight, Coins, Info, Package, Search, Landmark } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { bonndleAuthApi } from '../services/bonndleAuthApi';
import { defaultsApi, paymentApi } from '../services/api';
import { getStoredUser, getAuthToken, setAuthToken, STORAGE_KEYS } from '../services/bonndleApi';

const API_BASE = import.meta.env.VITE_API_URL || 'https://boldscaleservices.com';

// Determine environment from API base URL (used for Wix stoken endpoint)
const API_ENV = API_BASE.includes('staging') ? 'staging' : 'prod';

// Get user auth token from localStorage or URL params (set during login, injected by Flutter, or passed via URL)
function getUserToken(urlToken = null) {
  // Check URL params first (for Flutter Web where localStorage injection isn't possible)
  if (urlToken) {
    console.log('[StorefrontPage] Using auth token from URL params');
    return urlToken;
  }
  // Check PWA storage keys (includes Flutter-injected token via bonndle:access_token)
  const token = getAuthToken() ||
         localStorage.getItem('bonndle_access_token') ||
         localStorage.getItem('bonndle_token');
  console.log('[StorefrontPage] getUserToken found token in storage:', !!token);
  return token;
}

// Wix API endpoint for fetching merchant products
const WIX_PRODUCTS_API = 'https://boldscaleservices.wixsite.com/workspaces/_functions/productsByMerchant';

// Wix API endpoint for fetching admin token (same as Flutter uses)
const WIX_STOKEN_API = 'https://boldscaleservices.wixsite.com/workspaces/_functions/getStoken';
const WIX_STOKEN_BEARER = 'bsclw1x@pisvc_90uhqwd0164hb1307913hhj92743r2u49uniu2h3gr99_';

// Wix API endpoint for posting comments/system messages to groups
const WIX_COMMENTS_API = 'https://boldscaleservices.wixsite.com/workspaces/_functions/bonndleComments';

// Fetch admin token from Wix endpoint (used for /store-details API)
async function fetchAdminToken() {
  try {
    const endpoint = `${WIX_STOKEN_API}?env=${API_ENV}`;
    console.log('[StorefrontPage] Fetching admin token from Wix endpoint...');

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${WIX_STOKEN_BEARER}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error('[StorefrontPage] Failed to fetch admin token:', response.status);
      return null;
    }

    const data = await response.json();
    const token = data.stoken || data.token || data;

    if (token && typeof token === 'string') {
      console.log('[StorefrontPage] Successfully fetched admin token from Wix');
      return token;
    }

    console.error('[StorefrontPage] Invalid admin token response:', data);
    return null;
  } catch (error) {
    console.error('[StorefrontPage] Error fetching admin token:', error);
    return null;
  }
}

// Post system message to group announcing the purchase
async function postSystemMessageToGroup(groupId, userTag, savedAmount, merchantName) {
  if (!groupId) {
    console.log('[StorefrontPage] No groupId provided, skipping system message');
    return;
  }

  try {
    const announcementText = `@${userTag} just saved ₦${savedAmount.toLocaleString()} on a ${merchantName} purchase`;
    console.log('[StorefrontPage] Posting system message to group:', groupId, 'text:', announcementText);

    const response = await fetch(WIX_COMMENTS_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        feedItemId: groupId,
        author: 'bonndleagent',
        text: announcementText,
      }),
    });

    if (!response.ok) {
      console.error('[StorefrontPage] Failed to post system message:', response.status);
      return;
    }

    console.log('[StorefrontPage] System message posted successfully');
  } catch (error) {
    // Don't fail the transaction if announcement fails
    console.error('[StorefrontPage] Error posting system message:', error);
  }
}

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
}

export default function StorefrontPage() {
  const { merchantId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cashbackCodeFromUrl = searchParams.get('code');
  // Cashback percent from URL (passed from Flutter when navigating to store)
  const cashbackPercentFromUrl = searchParams.get('cashbackPercent') ? parseFloat(searchParams.get('cashbackPercent')) : null;
  // Auth params from URL (used when opened from Flutter Web - can't inject into external browser localStorage)
  const authTokenFromUrl = searchParams.get('authToken');
  const adminTokenFromUrl = searchParams.get('adminToken'); // Admin token for /store-details (normally injected by Flutter)
  const apiKeyFromUrl = searchParams.get('apiKey');
  // Actual backend merchantId (may differ from URL path slug)
  const merchantIdFromUrl = searchParams.get('merchantId');
  // Group ID for posting system messages after transaction
  const groupIdFromUrl = searchParams.get('groupId');
  // Check if opened from Flutter mobile WebView (embedded=flutter param)
  const isEmbeddedFlutter = searchParams.get('embedded') === 'flutter';

  console.log('[StorefrontPage] URL params - authToken:', !!authTokenFromUrl, 'adminToken:', !!adminTokenFromUrl, 'apiKey:', !!apiKeyFromUrl, 'merchantId:', merchantIdFromUrl, 'groupId:', groupIdFromUrl, 'embedded:', isEmbeddedFlutter, 'cashbackPercent:', cashbackPercentFromUrl);

  // Store URL auth token in localStorage so apiFetch and paymentApi can use it
  // This is essential for WebView and Flutter Web where token is passed via URL params
  useEffect(() => {
    if (authTokenFromUrl) {
      console.log('[StorefrontPage] Storing auth token from URL params in localStorage');
      setAuthToken(authTokenFromUrl);
    }
  }, [authTokenFromUrl]);

  // Storage keys for Flutter-injected auth data (injected via JavaScript, not URL)
  const FLUTTER_STORAGE_KEYS = {
    REWARDS_BALANCE: 'bonndle:rewards_balance',
    SAVED_CARDS: 'bonndle:saved_cards',
    FLUTTER_INJECTED: 'bonndle:flutter_injected',
    FLUTTER_LOADING: 'bonndle:flutter_loading',
    MERCHANT_API_KEY: 'bonndle:merchant_api_key',
    ADMIN_TOKEN: 'bonndle:admin_token',
    DISCOUNT_CODE: 'bonndle:discount_code',
    CASHBACK_CODE: 'bonndle:cashback_code',
  };

  // State to track when Flutter injects auth data
  const [flutterAuthReady, setFlutterAuthReady] = useState(false);

  // Admin token fetched from Wix endpoint (for /store-details API)
  const [fetchedAdminToken, setFetchedAdminToken] = useState(null);

  // Fetch admin token on page load if not available from Flutter/URL
  useEffect(() => {
    const getAdminToken = async () => {
      // Check if already available from Flutter localStorage or URL params
      const existingToken = localStorage.getItem(FLUTTER_STORAGE_KEYS.ADMIN_TOKEN) || adminTokenFromUrl;
      if (existingToken) {
        console.log('[StorefrontPage] Admin token already available, skipping fetch');
        return;
      }

      // Fetch from Wix endpoint
      console.log('[StorefrontPage] Fetching admin token on page load...');
      const token = await fetchAdminToken();
      if (token) {
        setFetchedAdminToken(token);
        console.log('[StorefrontPage] Admin token fetched and stored in state');
      }
    };

    getAdminToken();
  }, [adminTokenFromUrl]);

  // Product states - fetched from API
  const [products, setProducts] = useState([]);
  const [merchantName, setMerchantName] = useState('Store');
  const [merchantLogo, setMerchantLogo] = useState('https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=100');
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState(null);

  // Fetch products from Wix API
  useEffect(() => {
    const fetchProducts = async () => {
      const actualMerchantId = merchantIdFromUrl || merchantId;

      if (!actualMerchantId) {
        console.error('[StorefrontPage] No merchantId available');
        setProductsError('No merchant ID provided');
        setProductsLoading(false);
        return;
      }

      console.log('[StorefrontPage] Fetching products for merchantId:', actualMerchantId);
      setProductsLoading(true);
      setProductsError(null);

      try {
        const response = await fetch(`${WIX_PRODUCTS_API}?merchantId=${actualMerchantId}`);

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }

        const data = await response.json();
        console.log('[StorefrontPage] Products API response:', data);

        // Handle different response formats
        let fetchedProducts = [];
        let fetchedMerchantName = 'Store';
        let fetchedMerchantLogo = null;

        if (Array.isArray(data)) {
          // Response is array of products directly
          fetchedProducts = data;
        } else if (data.products && Array.isArray(data.products)) {
          // Response has products array
          fetchedProducts = data.products;
          fetchedMerchantName = data.merchantName || data.merchant_name || 'Store';
          fetchedMerchantLogo = data.merchantLogo || data.merchant_logo || data.logo;
        } else if (data.items && Array.isArray(data.items)) {
          // Alternative format with items array
          fetchedProducts = data.items;
          fetchedMerchantName = data.merchantName || data.merchant_name || 'Store';
          fetchedMerchantLogo = data.merchantLogo || data.merchant_logo || data.logo;
        }

        // Transform products to expected format
        // Note: Wix API uses 'product' field for the title/name
        const transformedProducts = fetchedProducts.map((item, index) => ({
          id: item.id || item._id || `product_${index}`,
          title: item.product || item.title || item.name || item.productName || 'Untitled Product',
          description: item.description || item.productDescription || '',
          images: item.images || item.mediaItems?.map(m => m.url || m.src) ||
                  (item.image ? [item.image] : ['https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400']),
          amount: item.unitPrice || item.amount || item.price || item.productPrice ||
                  (item.priceData?.price ? item.priceData.price * 100 : 0),
          originalAmount: item.originalAmount || item.originalPrice || item.compareAtPrice || null,
          dealBadge: item.dealBadge || item.badge || item.discountBadge || null,
          category: item.category || item.productCategory || 'General',
          size: item.size || null,
          unitQty: item.unitQty || item.unit_qty || 1,
          inStock: item.inStock !== undefined ? (typeof item.inStock === 'number' ? item.inStock > 0 : item.inStock) :
                   (item.stock !== undefined ? item.stock > 0 : true),
        }));

        console.log('[StorefrontPage] Transformed products:', transformedProducts.length);

        setProducts(transformedProducts);

        if (fetchedMerchantName) {
          setMerchantName(fetchedMerchantName);
        }
        if (fetchedMerchantLogo) {
          setMerchantLogo(fetchedMerchantLogo);
        }

      } catch (error) {
        console.error('[StorefrontPage] Error fetching products:', error);
        setProductsError(error.message);
        setProducts([]);
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [merchantId, merchantIdFromUrl]);

  // Listen for Flutter auth injection event (secure method - data injected into localStorage via JS)
  useEffect(() => {
    const handleFlutterAuthInjected = (event) => {
      console.log('[StorefrontPage] Flutter auth data injected into localStorage', event?.detail);
      setFlutterAuthReady(true);

      // Check if Flutter also injected a discount code
      const flutterCode = localStorage.getItem(FLUTTER_STORAGE_KEYS.DISCOUNT_CODE) ||
                          localStorage.getItem(FLUTTER_STORAGE_KEYS.CASHBACK_CODE);
      if (flutterCode && !cashbackCode) {
        console.log('[StorefrontPage] Using discount code from Flutter localStorage:', flutterCode);
        setCashbackCode(flutterCode);
      }
    };

    // Handle discount code ready event from Flutter
    const handleDiscountCodeReady = (event) => {
      const code = event?.detail?.code;
      console.log('[StorefrontPage] Flutter discount code ready event:', code);
      if (code && !cashbackCode) {
        setCashbackCode(code);
      }
    };

    // Check if Flutter is still loading (wait for auth)
    const flutterLoading = localStorage.getItem(FLUTTER_STORAGE_KEYS.FLUTTER_LOADING) === 'true';
    if (flutterLoading) {
      console.log('[StorefrontPage] Flutter is loading auth data, waiting...');
    }

    // Check if Flutter already injected data before this component mounted
    if (localStorage.getItem(FLUTTER_STORAGE_KEYS.FLUTTER_INJECTED) === 'true') {
      console.log('[StorefrontPage] Flutter auth data already present in localStorage');
      setFlutterAuthReady(true);

      // Also check for pre-injected discount code
      const flutterCode = localStorage.getItem(FLUTTER_STORAGE_KEYS.DISCOUNT_CODE) ||
                          localStorage.getItem(FLUTTER_STORAGE_KEYS.CASHBACK_CODE);
      if (flutterCode && !cashbackCodeFromUrl) {
        console.log('[StorefrontPage] Found discount code in Flutter localStorage:', flutterCode);
        setCashbackCode(flutterCode);
      }
    }

    // Listen for the custom event dispatched by Flutter WebView
    window.addEventListener('bonndleAuthInjected', handleFlutterAuthInjected);

    // Listen for discount code ready event from Flutter
    window.addEventListener('bonndleDiscountCodeReady', handleDiscountCodeReady);

    // Listen for Flutter cart open event
    const handleFlutterOpenCart = () => {
      console.log('[StorefrontPage] Flutter requested to open cart');
      setShowCart(true);
    };
    window.addEventListener('bonndleOpenCart', handleFlutterOpenCart);

    // Listen for Flutter search event
    const handleFlutterSearch = (event) => {
      const query = event?.detail?.query || '';
      console.log('[StorefrontPage] Flutter search query:', query);
      setSearchQuery(query);
    };
    window.addEventListener('bonndleSearch', handleFlutterSearch);

    return () => {
      window.removeEventListener('bonndleAuthInjected', handleFlutterAuthInjected);
      window.removeEventListener('bonndleDiscountCodeReady', handleDiscountCodeReady);
      window.removeEventListener('bonndleOpenCart', handleFlutterOpenCart);
      window.removeEventListener('bonndleSearch', handleFlutterSearch);
    };
  }, []);

  // State
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cashbackCode, setCashbackCode] = useState(cashbackCodeFromUrl || '');
  const [appliedCashback, setAppliedCashback] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);

  // Deferred code generation state (groupId + dealId from Flutter localStorage)
  const [dealInfo, setDealInfo] = useState(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [codeGenerationError, setCodeGenerationError] = useState(null);

  // Search state (for Flutter WebView search bar)
  const [searchQuery, setSearchQuery] = useState('');

  // API states
  const [validatingCode, setValidatingCode] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Payment methods state
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [rewardsBalance, setRewardsBalance] = useState(0);
  const [addingCard, setAddingCard] = useState(false);

  // Calculate rewards in USD (1000 points = $1 USD)
  const rewardsInUSD = useMemo(() => {
    return rewardsBalance / 1000;
  }, [rewardsBalance]);

  // Filter products based on search query (from Flutter WebView search bar)
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) {
      return products;
    }
    const query = searchQuery.toLowerCase().trim();
    return products.filter((product) => {
      const title = (product.title || '').toLowerCase();
      const category = (product.category || '').toLowerCase();
      const description = (product.description || '').toLowerCase();
      return title.includes(query) || category.includes(query) || description.includes(query);
    });
  }, [products, searchQuery]);

  // Auto-apply cashback code and percentage from URL on mount
  // When Flutter navigates to the store, it passes both code and cashbackPercent in URL
  useEffect(() => {
    if (cashbackCodeFromUrl && !appliedCashback) {
      setCashbackCode(cashbackCodeFromUrl);

      // If cashback percent is also provided in URL, auto-apply the cashback
      // This avoids needing to call the validate API when we already know the percentage
      if (cashbackPercentFromUrl && cashbackPercentFromUrl > 0) {
        console.log('[StorefrontPage] Auto-applying cashback from URL params:', cashbackCodeFromUrl, cashbackPercentFromUrl + '%');
        setAppliedCashback({
          code: cashbackCodeFromUrl,
          percentage: cashbackPercentFromUrl,
          message: `${cashbackPercentFromUrl}% cashback applied`,
        });
      }
    }
  }, [cashbackCodeFromUrl, cashbackPercentFromUrl]);

  // Read deal info from localStorage for deferred code generation
  // Flutter WebView injects: bonndle:group_id, bonndle:deal_id, bonndle:cashback_percent, bonndle:min_purchase_amount
  useEffect(() => {
    const groupId = localStorage.getItem('bonndle:group_id');
    const dealId = localStorage.getItem('bonndle:deal_id');
    const storedCashbackPercent = localStorage.getItem('bonndle:cashback_percent');
    const minPurchaseAmount = localStorage.getItem('bonndle:min_purchase_amount');

    // Only use deal info if we don't already have a code from URL
    if (groupId && dealId && !cashbackCodeFromUrl) {
      console.log('[StorefrontPage] Found deal info in localStorage for deferred code generation:', { groupId, dealId });
      setDealInfo({
        groupId,
        dealId,
        cashbackPercent: storedCashbackPercent ? parseFloat(storedCashbackPercent) : cashbackPercentFromUrl,
        minPurchaseAmount: minPurchaseAmount ? parseFloat(minPurchaseAmount) : null,
      });
    }
  }, [cashbackCodeFromUrl, cashbackPercentFromUrl]);

  // Generate cashback code via API (deferred generation)
  const generateCashbackCode = async () => {
    if (!dealInfo?.groupId || !dealInfo?.dealId) {
      console.log('[StorefrontPage] No deal info available for code generation');
      return null;
    }

    const token = getUserToken(authTokenFromUrl);
    if (!token) {
      setCodeGenerationError('Please log in to generate your cashback code');
      return null;
    }

    setIsGeneratingCode(true);
    setCodeGenerationError(null);

    try {
      console.log('[StorefrontPage] Generating cashback code for deal:', dealInfo.dealId);
      const response = await fetch(`${API_BASE}/group-code/${dealInfo.groupId}/deal/${dealInfo.dealId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Failed to generate code');
      }

      const data = await response.json();
      console.log('[StorefrontPage] Code generated successfully:', data);

      const generatedCode = data.code || data.discountCode || data.discount_code;
      if (generatedCode) {
        setCashbackCode(generatedCode);
        // Auto-apply the generated code
        setAppliedCashback({
          code: generatedCode,
          percentage: dealInfo.cashbackPercent || data.percentage || data.cashbackPercent || 10,
          message: `${dealInfo.cashbackPercent || data.percentage || 10}% cashback applied`,
        });
        return generatedCode;
      } else {
        throw new Error('No code received from server');
      }
    } catch (error) {
      console.error('[StorefrontPage] Failed to generate cashback code:', error);
      setCodeGenerationError(error.message || 'Failed to generate cashback code');
      return null;
    } finally {
      setIsGeneratingCode(false);
    }
  };

  // Fetch saved cards and rewards balance
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setCardsLoading(true);
      try {
        // Check if data was injected from Flutter via localStorage (secure method)
        // This takes priority as it's the most up-to-date from the Flutter app
        const flutterRewards = localStorage.getItem(FLUTTER_STORAGE_KEYS.REWARDS_BALANCE);
        const flutterCards = localStorage.getItem(FLUTTER_STORAGE_KEYS.SAVED_CARDS);

        if (flutterRewards) {
          const rewards = parseFloat(flutterRewards) || 0;
          console.log('[StorefrontPage] Using rewards from Flutter localStorage:', rewards);
          setRewardsBalance(rewards);
        }

        if (flutterCards) {
          try {
            const cards = JSON.parse(flutterCards);
            if (Array.isArray(cards) && cards.length > 0) {
              console.log('[StorefrontPage] Using cards from Flutter localStorage:', cards.length);
              setSavedCards(cards);
              setCardsLoading(false);
              // If we have data from Flutter, we can skip API fetch for cards
              if (flutterRewards) return;
            }
          } catch (e) {
            console.warn('[StorefrontPage] Failed to parse cards from Flutter localStorage:', e);
          }
        }

        // Check if user is authenticated (has token)
        const hasToken = !!getAuthToken();
        console.log('[StorefrontPage] Has auth token:', hasToken);

        // Fetch cards, defaults, and user profile in parallel
        const [cardsResult, defaultsResult, profileResult] = await Promise.allSettled([
          hasToken ? bonndleAuthApi.getCards() : Promise.resolve([]),
          defaultsApi.get(),
          // Also fetch user profile directly from API to ensure we have latest data
          // This is important when page is opened externally (e.g., from Flutter app)
          hasToken ? bonndleAuthApi.getProfile().catch(() => null) : Promise.resolve(null),
        ]);

        // Only use API cards if we didn't get them from Flutter
        if (!flutterCards && cardsResult.status === 'fulfilled') {
          const cards = Array.isArray(cardsResult.value) ? cardsResult.value : [];
          setSavedCards(cards);
          console.log('[StorefrontPage] Loaded saved cards from API:', cards.length);
        }

        // Get rewards balance - try multiple sources in order of priority:
        // 1. Flutter localStorage injection - already handled above
        // 2. Fresh API profile data
        // 3. Context user (from AuthContext)
        // 4. Stored user data (localStorage)
        if (!flutterRewards) {
          let balance = 0;
          if (profileResult.status === 'fulfilled' && profileResult.value) {
            balance = profileResult.value.available_bold_credits ||
                      profileResult.value.bold_credits ||
                      profileResult.value.boldCredits || 0;
            console.log('[StorefrontPage] Rewards balance from API profile:', balance);
          } else if (user) {
            balance = user.available_bold_credits || user.bold_credits || user.boldCredits || 0;
            console.log('[StorefrontPage] Rewards balance from context user:', balance);
          } else {
            // Fallback: try to get from localStorage stored user data
            const storedUser = getStoredUser();
            if (storedUser) {
              // Parse stored user data if it's a string
              const userData = typeof storedUser === 'string' ? JSON.parse(storedUser) : storedUser;
              balance = userData?.available_bold_credits || userData?.bold_credits || userData?.boldCredits || 0;
              console.log('[StorefrontPage] Rewards balance from stored user:', balance);
            }
          }
          setRewardsBalance(balance);
        }
      } catch (err) {
        console.error('Failed to fetch payment methods:', err);
      } finally {
        setCardsLoading(false);
      }
    };

    fetchPaymentMethods();
  }, [flutterAuthReady, user]); // Re-run when Flutter injects auth data or user changes

  // Generate unique payment reference
  const generatePaymentReference = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const suffix = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return `TT${suffix}`;
  };

  // Handle Add New Card - initialize payment and open Paystack popup
  const handleAddNewCard = async () => {
    if (addingCard) return;

    setAddingCard(true);
    try {
      const paymentReference = generatePaymentReference();
      console.log('[StorefrontPage] Initializing add card payment with reference:', paymentReference);

      // Initialize payment with small verification amount
      const initResponse = await paymentApi.initializePayment({
        amount: 100, // 100 NGN for card verification
        paymentReference,
        currencyCode: 'NGN',
        channel: 'card',
      });

      console.log('[StorefrontPage] Initialize payment response:', initResponse);

      // Extract payment URL from response
      const paymentUrl = initResponse.url ||
                         initResponse.authorizationUrl ||
                         initResponse.authorization_url ||
                         initResponse.data?.url ||
                         initResponse.data?.authorization_url;

      if (paymentUrl) {
        // Open payment URL in a popup window
        const popup = window.open(paymentUrl, 'paystack_popup', 'width=500,height=600,scrollbars=yes,resizable=yes');

        // Poll to check when popup is closed
        const pollTimer = setInterval(() => {
          if (popup?.closed) {
            clearInterval(pollTimer);
            console.log('[StorefrontPage] Payment popup closed, refreshing cards...');

            // Refresh saved cards after popup closes
            setTimeout(async () => {
              try {
                const cards = await bonndleAuthApi.getCards();
                if (Array.isArray(cards)) {
                  setSavedCards(cards);
                  console.log('[StorefrontPage] Cards refreshed:', cards.length);

                  // Auto-select the new card if it's the first one
                  if (cards.length > 0 && !selectedPayment) {
                    const defaultCard = cards.find((c) => c.isDefaultCard) || cards[0];
                    setSelectedPayment({
                      id: defaultCard.uuid || defaultCard.id,
                      type: 'card',
                      last4: defaultCard.last4Digits || defaultCard.last4,
                      cardProvider: defaultCard.cardProvider || defaultCard.brand || 'Card',
                    });
                  }
                }
              } catch (err) {
                console.error('[StorefrontPage] Failed to refresh cards:', err);
              }
              setAddingCard(false);
            }, 1000); // Small delay to allow backend to process
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollTimer);
          if (!popup?.closed) {
            popup?.close();
          }
          setAddingCard(false);
        }, 300000);
      } else {
        throw new Error('No payment URL received');
      }
    } catch (err) {
      console.error('[StorefrontPage] Failed to add card:', err);
      alert('Failed to add card. Please try again.');
      setAddingCard(false);
    }
  };

  // Cart functions
  const addToCart = (product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    setCart((prev) =>
      prev
        .map((item) =>
          item.product.id === productId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const cartTotal = cart.reduce(
    (sum, item) => sum + item.product.amount * item.quantity,
    0
  );

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Check if minimum purchase is met for deferred code generation
  const meetsMinimumPurchase = useMemo(() => {
    if (!dealInfo?.minPurchaseAmount) return true;
    return cartTotal >= dealInfo.minPurchaseAmount;
  }, [dealInfo, cartTotal]);

  // Notify Flutter WebView when cart count changes
  useEffect(() => {
    // Post to Flutter cart channel if available
    if (window.FlutterCartChannel) {
      window.FlutterCartChannel.postMessage(cartItemCount.toString());
      console.log('[StorefrontPage] Notified Flutter of cart count:', cartItemCount);
    }

    // Also dispatch custom event for Flutter to listen
    window.dispatchEvent(new CustomEvent('bonndleCartUpdate', {
      detail: { count: cartItemCount }
    }));

    // Store in localStorage for Flutter to read
    localStorage.setItem('bonndle:cart_count', cartItemCount.toString());
  }, [cartItemCount]);

  // Calculate cashback amount (earned AFTER purchase, NOT subtracted from total)
  // Use appliedCashback percentage first, then fall back to URL param, then dealInfo (from localStorage)
  const cashbackPercent = appliedCashback?.percentage || cashbackPercentFromUrl || dealInfo?.cashbackPercent || 0;
  const cashbackAmount = (cartTotal * cashbackPercent) / 100;

  // Final total is the FULL cart amount (cashback is earned, not discounted)
  const finalTotal = cartTotal;

  // Store transaction by calling Boldscale API directly
  // Uses Flutter-injected admin token, URL params, or pre-fetched token from Wix endpoint
  const storeTransactionViaApi = async (transactionData) => {
    const actualMerchantId = merchantIdFromUrl || merchantId;

    // Get admin token from: Flutter localStorage > URL params > pre-fetched from Wix
    const adminToken = localStorage.getItem(FLUTTER_STORAGE_KEYS.ADMIN_TOKEN) || adminTokenFromUrl || fetchedAdminToken;
    const merchantApiKey = localStorage.getItem(FLUTTER_STORAGE_KEYS.MERCHANT_API_KEY) || apiKeyFromUrl;

    // If no admin token available, we can't store the transaction
    if (!adminToken) {
      console.log('[StorefrontPage] No admin token available from any source, skipping transaction store');
      console.log('[StorefrontPage] Transaction will still complete, but cashback won\'t be tracked server-side');
      return { success: true, skipped: true, message: 'No admin credentials - transaction stored locally only' };
    }

    const tokenSource = localStorage.getItem(FLUTTER_STORAGE_KEYS.ADMIN_TOKEN) ? 'localStorage' : (adminTokenFromUrl ? 'URL params' : 'Wix endpoint (pre-fetched)');
    console.log('[StorefrontPage] Admin token source:', tokenSource);

    const payload = {
      merchantId: actualMerchantId,
      discountCode: transactionData.discountCode,
      merchantResponse: transactionData.merchantResponse,
    };

    console.log('[StorefrontPage] Storing transaction via API:', payload);

    try {
      const response = await fetch(`${API_BASE}/group-code/merchant/store-details`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
          ...(merchantApiKey && { 'x-api-key': merchantApiKey }),
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('[StorefrontPage] API store-details error:', data);
        // Don't fail the whole transaction, just log the error
        return { success: false, error: data.error || data.message || 'Failed to store transaction' };
      }

      console.log('[StorefrontPage] Transaction stored successfully via API:', data);
      return { success: true, data };
    } catch (error) {
      console.error('[StorefrontPage] API error:', error);
      // Don't fail the whole transaction, just log the error
      return { success: false, error: error.message };
    }
  };

  // Validate cashback code via API
  // NOTE: /validate endpoint requires USER token only - no merchant API key needed
  const validateCashbackCode = async () => {
    if (!cashbackCode.trim()) return;

    setValidatingCode(true);
    setValidationError(null);

    try {
      const token = getUserToken(authTokenFromUrl);
      console.log('[StorefrontPage] Validating cashback code:', cashbackCode.trim());
      console.log('[StorefrontPage] Using user token:', !!token);

      if (!token) {
        throw new Error('Please log in to apply cashback codes.');
      }

      const response = await fetch(`${API_BASE}/group-code/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ discountCode: cashbackCode.trim() }),
      });

      console.log('[StorefrontPage] Validation response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[StorefrontPage] Validation error:', errorData);
        throw new Error(errorData.message || errorData.error || 'Invalid cashback code');
      }

      const data = await response.json();
      console.log('[StorefrontPage] Validation success:', data);

      const appliedData = {
        code: cashbackCode,
        percentage: data.discount || data.discountPercentage || data.discount_percentage || data.cashback || data.cashbackPercent || 10,
        message: data.message || 'Cashback code applied successfully!',
        validUntil: data.validUntil || data.valid_until,
      };

      setAppliedCashback(appliedData);

      // Notify Flutter WebView that code was applied
      if (window.FlutterTransactionChannel) {
        window.FlutterTransactionChannel.postMessage(JSON.stringify({
          type: 'code_applied',
          code: cashbackCode,
          percentage: appliedData.percentage,
        }));
        console.log('[StorefrontPage] Notified Flutter of code application:', cashbackCode);
      }
    } catch (error) {
      console.error('[StorefrontPage] Cashback validation error:', error);
      setValidationError(error.message || 'Failed to validate cashback code');
    } finally {
      setValidatingCode(false);
    }
  };

  const copyCashbackCode = async () => {
    const codeToCopy = cashbackCodeFromUrl || cashbackCode;
    if (codeToCopy) {
      await navigator.clipboard.writeText(codeToCopy);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    // If we have deal info but no code yet, generate it now (deferred generation)
    if (dealInfo && !cashbackCode && !appliedCashback && meetsMinimumPurchase) {
      console.log('[StorefrontPage] Generating cashback code at checkout...');
      await generateCashbackCode();
    }

    // New flow: go to payment selection first
    setShowPayment(true);
  };

  const handleProceedToCheckoutSummary = async () => {
    if (!selectedPayment) {
      alert('Please select a payment method');
      return;
    }

    // Auto-generate cashback code if deal info exists but no code yet
    // This ensures the code is ready before showing checkout summary
    if (dealInfo && !cashbackCode && !appliedCashback && meetsMinimumPurchase) {
      console.log('[StorefrontPage] Auto-generating cashback code before checkout summary...');
      await generateCashbackCode();
    }

    // Go from payment selection to checkout summary
    setShowPayment(false);
    setShowCheckout(true);
  };

  const handleBackToPayment = () => {
    setShowCheckout(false);
    setShowPayment(true);
  };

  // Store transaction via API using Flutter-injected credentials
  // When opened from Flutter WebView, admin token and API key are injected into localStorage
  // When opened standalone (web browser), transaction storage is skipped but purchase still works
  const storeTransaction = async (transactionId) => {
    // Only store if a cashback code was applied
    const codeToStore = appliedCashback?.code || cashbackCode;
    if (!codeToStore) {
      console.log('[StorefrontPage] No cashback code to store, skipping transaction store');
      return null;
    }

    try {
      const actualMerchantId = merchantIdFromUrl || merchantId;
      console.log('[StorefrontPage] Using merchantId for transaction:', actualMerchantId);

      // Build products array with all cart item details
      const productsArray = cart.map((item) => ({
        productId: item.product.id || item.product.product_uuid,
        productName: item.product.title,
        productDescription: item.product.description || '',
        quantity: item.quantity,
        unitPrice: item.product.amount,
        totalPrice: item.product.amount * item.quantity,
      }));

      const transactionData = {
        discountCode: codeToStore,
        merchantResponse: {
          status: 'approved',
          transactionId: transactionId,
          transactionAmount: finalTotal,
          cashbackAmount: cashbackAmount,
          details: {
            merchantId: actualMerchantId,
            cashbackPercent: cashbackPercent,
            validUntil: '2026-12-31',
            totalPrice: finalTotal,
            products: productsArray, // Array of all purchased products
          },
        },
      };

      // Call the API directly to store transaction
      // Uses Flutter-injected credentials when available
      const result = await storeTransactionViaApi(transactionData);

      if (result.skipped) {
        console.log('[StorefrontPage] Transaction store skipped (no admin credentials)');
        return { skipped: true };
      }

      if (!result.success) {
        console.error('[StorefrontPage] Failed to store transaction:', result.error);
        // Don't return null - the payment was still successful, just the tracking failed
        return { error: result.error };
      }

      console.log('[StorefrontPage] Transaction stored successfully');

      // Post system message to group (if groupId provided and not a rewards payment)
      // Only post when NOT opened in Flutter WebView (Flutter handles its own system messages)
      if (groupIdFromUrl && !isEmbeddedFlutter && selectedPayment?.id !== 'rewards') {
        const storedUser = getStoredUser();
        const userTag = storedUser?.boldTag || storedUser?.firstName || user?.boldTag || user?.firstName || 'A member';
        // Post in background - don't block on this
        postSystemMessageToGroup(groupIdFromUrl, userTag, cashbackAmount, merchantName).catch((e) => {
          console.error('[StorefrontPage] Background system message failed:', e);
        });
      }

      return result.data;
    } catch (error) {
      console.error('[StorefrontPage] Error storing transaction:', error);
      return null;
    }
  };

  // Process payment - handles rewards, card, and bank transfer
  const processPayment = async () => {
    if (!selectedPayment) {
      alert('Please select a payment method');
      return;
    }

    setProcessingPayment(true);

    try {
      let transactionId;

      // Handle bank transfer payment
      if (selectedPayment.id === 'bank_transfer') {
        // Step 1: Initialize payment with transfer channel
        const paymentReference = generatePaymentReference();
        console.log('[StorefrontPage] Bank transfer - Step 1: Initializing payment with reference:', paymentReference);

        const initResponse = await paymentApi.initializePayment({
          amount: finalTotal,
          paymentReference,
          currencyCode: 'NGN',
          channel: 'transfer',
        });
        console.log('[StorefrontPage] Bank transfer - Initialize response:', initResponse);

        // Get payment URL from response
        const paymentUrl = initResponse.url ||
                           initResponse.authorizationUrl ||
                           initResponse.authorization_url ||
                           initResponse.data?.url;

        if (!paymentUrl) {
          throw new Error('No payment URL received from server');
        }

        // Step 2: Open payment URL
        console.log('[StorefrontPage] Bank transfer - Step 2: Opening payment...');

        // Set up event listener for Flutter WebView payment complete signal
        let paymentCompleted = false;
        let paymentCancelled = false;
        const handlePaymentComplete = (event) => {
          console.log('[StorefrontPage] Received bonndlePaymentComplete event:', event.detail);
          paymentCompleted = true;
          if (event.detail?.cancelled) {
            paymentCancelled = true;
          }
        };
        window.addEventListener('bonndlePaymentComplete', handlePaymentComplete);

        try {
          // Try to open as popup - Flutter WebView will intercept and open in modal
          const popup = window.open(paymentUrl, 'bank_transfer_popup', 'width=500,height=700,scrollbars=yes,resizable=yes');

          // Wait for user to close popup or payment complete event
          await new Promise((resolve, reject) => {
            const pollTimer = setInterval(() => {
              // Check if popup closed (browser) or payment completed (Flutter)
              if (popup?.closed || paymentCompleted) {
                clearInterval(pollTimer);
                resolve();
              }
            }, 500);

            // Timeout after 10 minutes
            setTimeout(() => {
              clearInterval(pollTimer);
              if (popup && !popup.closed) {
                popup.close();
              }
              reject(new Error('Payment timeout'));
            }, 600000);
          });
        } finally {
          window.removeEventListener('bonndlePaymentComplete', handlePaymentComplete);
        }

        // Check if payment was cancelled
        if (paymentCancelled) {
          throw new Error('Payment cancelled by user');
        }

        console.log('[StorefrontPage] Bank transfer - Payment completed successfully');

        // Use payment reference as transaction ID
        // Note: For external merchant storefronts, we don't call purchase-with-transfer
        // since there are no internal product IDs. The store-details endpoint handles tracking.
        transactionId = `TRF_${paymentReference}`;
      } else if (selectedPayment.id === 'rewards') {
        // Handle rewards payment
        // For external merchant storefronts, rewards are deducted from balance
        // Note: Rewards payments don't earn cashback (already handled by clearing appliedCashback)
        transactionId = `RWD_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      } else {
        // Handle card payment
        // For external merchant storefronts, card payments are processed externally
        // We just generate a transaction ID for tracking - store-details handles cashback
        transactionId = `CRD_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
      }

      // Store the transaction via API (for cashback)
      await storeTransaction(transactionId);

      // Notify Flutter WebView that transaction is complete
      if (window.FlutterTransactionChannel) {
        window.FlutterTransactionChannel.postMessage(JSON.stringify({
          type: 'transaction_complete',
          transactionId: transactionId,
          amount: finalTotal,
          cashbackEarned: cashbackAmount,
          code: appliedCashback?.code || cashbackCode,
        }));
        console.log('[StorefrontPage] Notified Flutter of transaction completion:', transactionId);
      }

      // Set transaction details for success screen
      setTransactionDetails({
        transactionId,
        amount: finalTotal,
        cashbackEarned: cashbackAmount,
        cashbackPercent: cashbackPercent,
        items: cart,
        merchantName,
        cashbackCode: appliedCashback?.code || cashbackCode,
        paymentMethod: selectedPayment,
      });

      // Clear cart and show success
      setCart([]);
      setShowPayment(false);
      setShowCheckout(false);
      setShowSuccess(true);
      setAppliedCashback(null);
      setSelectedPayment(null);
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Payment failed. Please try again.');
      // Go back to payment selection on error
      setShowCheckout(false);
      setShowPayment(true);
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCloseSuccess = () => {
    // Notify Flutter that Done button was clicked (so it can navigate to group)
    if (window.FlutterTransactionChannel && transactionDetails) {
      try {
        window.FlutterTransactionChannel.postMessage(JSON.stringify({
          type: 'done_button_clicked',
          transactionId: transactionDetails.transactionId,
          cashbackEarned: transactionDetails.cashbackEarned,
          paymentMethod: transactionDetails.paymentMethod,
          merchantName: transactionDetails.merchantName,
          totalAmount: transactionDetails.amount,
          groupId: groupIdFromUrl, // Include groupId for navigation
        }));
        console.log('[PWA] Sent done_button_clicked to Flutter with groupId:', groupIdFromUrl);
      } catch (error) {
        console.error('[PWA] Error sending done_button_clicked:', error);
      }
    }
    setShowSuccess(false);
    setTransactionDetails(null);
    setShowCart(false);
  };

  // Check if opened from Flutter (hide header as Flutter has its own)
  // Check URL param first (embedded=flutter), then localStorage flags
  const isFromFlutter = isEmbeddedFlutter || flutterAuthReady || localStorage.getItem(FLUTTER_STORAGE_KEYS.FLUTTER_INJECTED) === 'true';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Hidden when opened from Flutter (Flutter has its own header) */}
      {!isFromFlutter && (
        <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <img
                src={merchantLogo}
                alt={merchantName}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="font-semibold text-gray-900">{merchantName}</h1>
                <p className="text-xs text-gray-500">Store</p>
              </div>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-indigo-600 text-white text-xs rounded-full flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>
          </div>

          {/* Cashback Code Banner */}
          {cashbackCodeFromUrl && (
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  <span className="text-sm">Your cashback code: <strong>{cashbackCodeFromUrl}</strong></span>
                </div>
                <button
                  onClick={copyCashbackCode}
                  className="flex items-center gap-1 text-xs bg-white/20 hover:bg-white/30 px-2 py-1 rounded transition-colors"
                >
                  {copiedCode ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copiedCode ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Products Grid */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Loading State */}
        {productsLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mb-4" />
            <p className="text-gray-500">Loading products...</p>
          </div>
        )}

        {/* Error State */}
        {!productsLoading && productsError && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <X className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load products</h3>
            <p className="text-gray-500 text-sm max-w-xs">{productsError}</p>
          </div>
        )}

        {/* Empty State */}
        {!productsLoading && !productsError && products.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Package className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No products available</h3>
            <p className="text-gray-500 text-sm max-w-xs">
              This merchant hasn't added any products yet. Check back later!
            </p>
          </div>
        )}

        {/* No Search Results State */}
        {!productsLoading && !productsError && products.length > 0 && filteredProducts.length === 0 && searchQuery && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Search className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-500 text-sm max-w-xs">
              No products match "{searchQuery}". Try a different search term.
            </p>
          </div>
        )}

        {/* Products Grid */}
        {!productsLoading && !productsError && filteredProducts.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={() => addToCart(product)}
                inCart={cart.some((item) => item.product.id === product.id)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Cart Drawer - Full Screen */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          {/* Cart Header */}
          <div className="p-4 border-b flex items-center justify-between bg-white">
            <h2 className="text-lg font-semibold">Your Cart ({cartItemCount})</h2>
            <button
              onClick={() => setShowCart(false)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cart.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Your cart is empty</p>
                </div>
              ) : (
                cart.map((item) => (
                  <CartItem
                    key={item.product.id}
                    item={item}
                    onUpdateQuantity={(delta) =>
                      updateQuantity(item.product.id, delta)
                    }
                    onRemove={() => removeFromCart(item.product.id)}
                  />
                ))
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t p-4 space-y-4">
                {/* Cashback Code Input */}
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cashbackCode}
                      onChange={(e) => setCashbackCode(e.target.value.toUpperCase())}
                      placeholder="Cashback code"
                      className="flex-1 px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={validateCashbackCode}
                      disabled={!cashbackCode.trim() || appliedCashback || validatingCode}
                      className="px-5 py-3 bg-indigo-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                      {validatingCode && <Loader2 className="w-4 h-4 animate-spin" />}
                      {validatingCode ? 'Validating...' : 'Apply'}
                    </button>
                  </div>

                  {validationError && (
                    <p className="text-red-500 text-xs">{validationError}</p>
                  )}
                </div>

                {/* Totals - Cashback shown as earned, not subtracted */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span>{formatCurrency(cartTotal)}</span>
                  </div>
                  {appliedCashback && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Cashback ({appliedCashback.percentage}%)</span>
                      <span>+{formatCurrency(cashbackAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2 border-t">
                    <span>Amount Due</span>
                    <span>{formatCurrency(finalTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
        </div>
      )}

      {/* Checkout Summary Page - Full screen view after payment method selection */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col">
          {/* Header */}
          <div className="p-4 border-b flex items-center gap-3 bg-white">
            <button
              onClick={handleBackToPayment}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold flex-1">Checkout Summary</h2>
            <button
              onClick={() => setShowCheckout(false)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-lg mx-auto p-4 space-y-4">
              {/* Cashback Code Section */}
              {(dealInfo || appliedCashback || cashbackCode) && (
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-bold text-emerald-700">
                      {appliedCashback?.percentage || dealInfo?.cashbackPercent
                        ? `${appliedCashback?.percentage || dealInfo?.cashbackPercent}% Cashback`
                        : 'Cashback Deal'}
                    </span>
                  </div>

                  {isGeneratingCode ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating your cashback code...</span>
                    </div>
                  ) : codeGenerationError ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-red-600">
                        <Info className="w-4 h-4" />
                        <span>{codeGenerationError}</span>
                      </div>
                      <button
                        onClick={generateCashbackCode}
                        className="text-sm text-indigo-600 font-medium hover:underline"
                      >
                        Try again
                      </button>
                    </div>
                  ) : (appliedCashback || cashbackCode) ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-emerald-200">
                        <span className="font-mono font-bold text-lg text-emerald-700 tracking-wide">
                          {appliedCashback?.code || cashbackCode}
                        </span>
                        <button
                          onClick={copyCashbackCode}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            copiedCode
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-emerald-600 text-white hover:bg-emerald-700'
                          }`}
                        >
                          {copiedCode ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                      {cashbackAmount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-emerald-600">You'll earn cashback</span>
                          <span className="font-bold text-emerald-700">
                            +{formatCurrency(cashbackAmount)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : dealInfo && !meetsMinimumPurchase && dealInfo.minPurchaseAmount ? (
                    <div className="text-sm text-amber-600">
                      Add {formatCurrency(dealInfo.minPurchaseAmount - cartTotal)} more to qualify for cashback
                    </div>
                  ) : dealInfo ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        Your cashback code will be generated when you complete checkout
                      </p>
                      <button
                        onClick={generateCashbackCode}
                        disabled={isGeneratingCode}
                        className="text-sm bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {isGeneratingCode && <Loader2 className="w-4 h-4 animate-spin" />}
                        Generate Code Now
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Manual Cashback Code Input (only if no deal info and no applied code) */}
              {!dealInfo && !appliedCashback && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-bold text-gray-700">Have a cashback code?</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={cashbackCode}
                      onChange={(e) => setCashbackCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="flex-1 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                      onClick={validateCashbackCode}
                      disabled={!cashbackCode.trim() || validatingCode}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-indigo-700 flex items-center gap-2"
                    >
                      {validatingCode && <Loader2 className="w-4 h-4 animate-spin" />}
                      Apply
                    </button>
                  </div>
                  {validationError && (
                    <p className="text-red-500 text-xs mt-2">{validationError}</p>
                  )}
                </div>
              )}

              {/* Order Items */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Order Items ({cartItemCount})
                </h3>
                <div className="space-y-3">
                  {cart.map((item) => (
                    <div key={item.product.id} className="flex gap-3">
                      <div className="w-16 h-16 rounded-lg bg-gray-50 p-1 flex-shrink-0">
                        <img
                          src={item.product.images[0]}
                          alt={item.product.title}
                          className="w-full h-full object-contain rounded"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">{item.product.title}</p>
                        <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                        <p className="text-sm font-semibold text-indigo-600">
                          {formatCurrency(item.product.amount * item.quantity)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h3 className="font-bold text-gray-900 mb-3">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span>{formatCurrency(cartTotal)}</span>
                  </div>
                  {appliedCashback && (
                    <div className="flex justify-between text-sm text-emerald-600">
                      <span className="flex items-center gap-1">
                        <Tag className="w-3 h-3" />
                        Cashback ({appliedCashback.percentage}%)
                      </span>
                      <span>+{formatCurrency(cashbackAmount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg pt-2 border-t">
                    <span>Amount Due</span>
                    <span>{formatCurrency(finalTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Selected Payment Method */}
              {selectedPayment && (
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" />
                    Payment Method
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {selectedPayment.id === 'rewards' ? (
                        <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="w-6 h-6" />
                      ) : selectedPayment.id === 'bank_transfer' ? (
                        <Landmark className="w-5 h-5 text-gray-600" />
                      ) : (
                        <CreditCard className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedPayment.id === 'rewards'
                          ? 'Bonndle Rewards'
                          : selectedPayment.id === 'bank_transfer'
                          ? 'Bank Transfer'
                          : `${selectedPayment.cardProvider || selectedPayment.brand || 'Card'} •••• ${selectedPayment.last4Digits || selectedPayment.last4 || '****'}`}
                      </p>
                      <button
                        onClick={handleBackToPayment}
                        className="text-xs text-indigo-600 font-medium hover:underline"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rewards info banner */}
              {selectedPayment && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                  selectedPayment.id === 'rewards'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-emerald-50 text-emerald-700'
                }`}>
                  {selectedPayment.id === 'rewards' ? (
                    <>
                      <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span>You will not earn rewards on this purchase</span>
                    </>
                  ) : cashbackAmount > 0 ? (
                    <>
                      <Coins className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <span>You will earn {formatCurrency(cashbackAmount)} cashback rewards</span>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Fixed Bottom - Pay Button */}
          <div className="border-t p-4 bg-white">
            <div className="max-w-lg mx-auto">
              <button
                onClick={processPayment}
                disabled={processingPayment}
                className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {processingPayment ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay ${formatCurrency(finalTotal)}`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal - Same as PayBillSlider */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => !processingPayment && setShowPayment(false)}
          />
          <div className="relative bg-white rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold">Select Payment Method</h2>
                <p className="text-sm text-gray-500">Choose how to pay</p>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between text-sm">
                <span>Amount Due</span>
                <span className="font-semibold text-lg">{formatCurrency(finalTotal)}</span>
              </div>
            </div>

            <div className="space-y-3">
              {/* Bonndle Rewards Payment Option */}
              <button
                onClick={() => {
                  if (rewardsBalance > 0) {
                    setSelectedPayment({ id: 'rewards', type: 'rewards', name: 'Bonndle Rewards' });
                    // Clear cashback code - rewards payments don't earn cashback (matches Flutter)
                    setCashbackCode('');
                    setAppliedCashback(null);
                  }
                }}
                disabled={rewardsBalance <= 0}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  selectedPayment?.id === 'rewards'
                    ? 'border-indigo-600 bg-indigo-50'
                    : rewardsBalance <= 0
                    ? 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                    : 'border-gray-100 bg-white hover:border-indigo-200'
                }`}
              >
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-100">
                  <img src="/logo/bonndleLogoIcon_Indigo.svg" alt="Bonndle" className="w-10 h-10" />
                </div>
                <div className="flex-1">
                  <p className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-gray-900' : 'text-gray-500'}`}>Bonndle Rewards</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      {Math.floor(rewardsBalance).toLocaleString()} Pts
                    </span>
                    <span className="text-gray-400">|</span>
                    <span className={`font-bold text-sm ${rewardsBalance > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                      ${rewardsInUSD.toFixed(2)}
                    </span>
                    {rewardsBalance <= 0 && (
                      <span className="text-xs text-gray-400 ml-1">• No balance</span>
                    )}
                  </div>
                </div>
                {selectedPayment?.id === 'rewards' && (
                  <Check className="w-5 h-5 text-indigo-600" />
                )}
              </button>

              {/* Saved Cards */}
              {!cardsLoading && savedCards.map((card) => {
                const cardBrand = card.cardProvider || card.brand || 'Card';
                const cardLast4 = card.last4Digits || card.last4 || '****';
                // Only show card as selected if selectedPayment is not a special type (bank_transfer, rewards)
                const isSelected = selectedPayment &&
                  !['bank_transfer', 'rewards'].includes(selectedPayment.id) &&
                  (selectedPayment.uuid === card.uuid || selectedPayment.id === card.id);

                return (
                  <button
                    key={card.uuid || card.id}
                    onClick={() => setSelectedPayment(card)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                      isSelected
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-100 bg-white hover:border-indigo-200'
                    }`}
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-sm text-gray-900">{cardBrand}</p>
                      <p className="text-xs text-gray-500">•••• {cardLast4}</p>
                    </div>
                    {isSelected && (
                      <Check className="w-5 h-5 text-indigo-600" />
                    )}
                  </button>
                );
              })}

              {/* Bank Transfer Payment Option */}
              {!cardsLoading && (
                <button
                  onClick={() => setSelectedPayment({ id: 'bank_transfer', type: 'bank_transfer', name: 'Bank Transfer' })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                    selectedPayment?.id === 'bank_transfer'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-100 bg-white hover:border-indigo-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    selectedPayment?.id === 'bank_transfer' ? 'bg-indigo-100' : 'bg-gray-100'
                  }`}>
                    <Landmark className={`w-5 h-5 ${selectedPayment?.id === 'bank_transfer' ? 'text-indigo-600' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-gray-900">Pay with Bank Transfer</p>
                    <p className="text-xs text-gray-500">Transfer from any Nigerian bank</p>
                  </div>
                  {selectedPayment?.id === 'bank_transfer' && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              )}

              {/* Test Card - Only show in staging when no saved cards */}
              {!cardsLoading && savedCards.length === 0 && API_ENV !== 'production' && (
                <button
                  onClick={() => setSelectedPayment({ id: 'test-card', type: 'card', last4: '4242', cardProvider: 'TEST' })}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                    selectedPayment?.id === 'test-card'
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-100 bg-white hover:border-indigo-200'
                  }`}
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-sm text-gray-900">Test Card</p>
                    <p className="text-xs text-gray-500">•••• 4242 (Demo)</p>
                  </div>
                  {selectedPayment?.id === 'test-card' && (
                    <Check className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              )}

              {cardsLoading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                </div>
              )}

              {/* Add New Card Button */}
              {!cardsLoading && (
                <button
                  onClick={handleAddNewCard}
                  disabled={addingCard}
                  className={`w-full p-3 rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-2 ${
                    addingCard
                      ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                      : 'border-gray-200 text-gray-500 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {addingCard ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="font-medium text-sm">Adding Card...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span className="font-medium text-sm">Add New Card</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Rewards info banner based on payment method */}
            {selectedPayment && (
              <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${
                selectedPayment.id === 'rewards'
                  ? 'bg-amber-50 text-amber-700'
                  : 'bg-emerald-50 text-emerald-700'
              }`}>
                {selectedPayment.id === 'rewards' ? (
                  <>
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <span>You will not earn rewards on this purchase</span>
                  </>
                ) : (
                  <>
                    <Coins className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <span>You will earn {formatCurrency(cashbackAmount)} cashback rewards on this purchase</span>
                  </>
                )}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowPayment(false)}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleProceedToCheckoutSummary}
                disabled={!selectedPayment}
                className="flex-1 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                Proceed
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Matches PayBillSlider design */}
      {showSuccess && transactionDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-white">
          <div className="w-full max-w-md text-center">
            <div className="p-6 pt-12">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Transaction Successful</h2>
              <p className="text-gray-500 text-sm mb-6">
                Your {transactionDetails.merchantName} purchase was successful.
              </p>

              {/* Receipt Card */}
              <div className="bg-gray-50 p-4 rounded-xl text-left mb-5">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Product</span>
                  <span className="text-sm font-bold text-gray-900 truncate max-w-[180px]">
                    {transactionDetails.items.length} item(s)
                  </span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Amount Paid</span>
                  <span className="text-sm font-bold text-gray-900">{formatCurrency(transactionDetails.amount)}</span>
                </div>
                {transactionDetails.cashbackEarned > 0 && (
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-500">Cashback Earned</span>
                    <span className="text-sm font-bold text-emerald-600">+{formatCurrency(transactionDetails.cashbackEarned)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Payment Method</span>
                  <span className="text-sm font-bold text-gray-900">
                    {transactionDetails.paymentMethod?.id === 'rewards'
                      ? 'Bonndle Rewards'
                      : transactionDetails.paymentMethod?.id === 'bank_transfer'
                      ? 'Bank Transfer'
                      : `Card (${transactionDetails.paymentMethod?.last4Digits || transactionDetails.paymentMethod?.last4 || '****'})`}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500">Reference</span>
                  <span className="text-sm font-bold text-gray-900">{transactionDetails.transactionId.slice(-12)}</span>
                </div>
              </div>

              {/* Savings Banner */}
              {transactionDetails.cashbackEarned > 0 && (
                <div className="bg-emerald-100 px-4 py-2 rounded-full inline-flex items-center gap-2">
                  <Coins size={16} className="text-amber-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="text-emerald-700 font-bold text-sm">
                    You saved {formatCurrency(transactionDetails.cashbackEarned)}
                  </span>
                  <Coins size={16} className="text-amber-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* Floating Cart Button (Mobile) */}
      {cartItemCount > 0 && !showCart && !showCheckout && !showPayment && !showSuccess && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 z-30 bg-indigo-600 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 hover:bg-indigo-700 transition-colors md:hidden"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-medium">{formatCurrency(cartTotal)}</span>
        </button>
      )}
    </div>
  );
}

// Product Card Component
function ProductCard({ product, onAddToCart, inCart }) {
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
      <div className="relative aspect-square p-[10px]">
        <img
          src={product.images[0]}
          alt={product.title}
          className="w-full h-full object-contain rounded-lg"
        />
        {product.dealBadge && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded">
            {product.dealBadge}
          </span>
        )}
        {!product.inStock && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="bg-white text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
              Out of Stock
            </span>
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-medium text-gray-900 text-sm line-clamp-2 mb-1">
          {product.unitQty && product.unitQty > 0 ? `${product.unitQty}x ` : ''}{product.title}
        </h3>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="font-semibold text-indigo-600">
            {formatCurrency(product.amount)}
          </span>
          {product.originalAmount && (
            <span className="text-xs text-gray-400 line-through">
              {formatCurrency(product.originalAmount)}
            </span>
          )}
        </div>
        <button
          onClick={onAddToCart}
          disabled={!product.inStock}
          className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
            inCart
              ? 'bg-indigo-100 text-indigo-700'
              : product.inStock
              ? 'bg-indigo-600 text-white hover:bg-indigo-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          {inCart ? 'In Cart' : product.inStock ? 'Add to Cart' : 'Unavailable'}
        </button>
      </div>
    </div>
  );
}

// Cart Item Component
function CartItem({ item, onUpdateQuantity, onRemove }) {
  const { product, quantity } = item;
  return (
    <div className="flex gap-3 bg-gray-50 rounded-xl p-3">
      <div className="w-20 h-20 rounded-lg bg-white p-[10px] flex-shrink-0">
        <img
          src={product.images[0]}
          alt={product.title}
          className="w-full h-full object-contain rounded"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm line-clamp-1">{product.title}</h3>
        <p className="text-indigo-600 font-semibold text-sm mt-1">
          {formatCurrency(product.amount)}
        </p>
        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => onUpdateQuantity(-1)}
            className="w-7 h-7 flex items-center justify-center bg-white border rounded-full hover:bg-gray-50"
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium w-6 text-center">{quantity}</span>
          <button
            onClick={() => onUpdateQuantity(1)}
            className="w-7 h-7 flex items-center justify-center bg-white border rounded-full hover:bg-gray-50"
          >
            <Plus className="w-4 h-4" />
          </button>
          <button
            onClick={onRemove}
            className="ml-auto text-gray-400 hover:text-red-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
